import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box,
} from '@mui/material';
import { WarningAmber } from '@mui/icons-material';

/**
 * Reusable confirmation dialog — replaces native window.confirm().
 *
 * Props:
 *   open       {boolean}   - whether the dialog is visible
 *   title      {string}    - dialog heading  (default: "Are you sure?")
 *   message    {string}    - body text
 *   confirmLabel {string}  - confirm button label (default: "Delete")
 *   confirmColor {string}  - MUI button color    (default: "error")
 *   onConfirm  {function}  - called when user clicks confirm
 *   onCancel   {function}  - called when user cancels / closes
 */
export default function ConfirmDialog({
  open,
  title = 'Are you sure?',
  message = 'This action cannot be undone.',
  confirmLabel = 'Delete',
  confirmColor = 'error',
  onConfirm,
  onCancel,
}) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2, flexShrink: 0,
            bgcolor: confirmColor === 'error' ? '#FEF2F2' : '#FFF9C4',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <WarningAmber sx={{ fontSize: 20, color: confirmColor === 'error' ? '#DC2626' : '#D97706' }} />
          </Box>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary">{message}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" size="small">Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color={confirmColor} size="small">
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
